import { ServerError } from '@azure/msal-browser'
import posthog from 'posthog-js'
import { handleErrorResponse } from '@/configs/client-error-handlers'
import { AuthState, useAuthStore } from '@/stores/auth-store'

/**
 * Source: https://orval.dev/reference/configuration/output#usedates
 * Related issue: https://github.com/orval-labs/orval/issues/1939
 * Typed fetcher for Orval-generated API functions.
 *
 * This function appends the Authorization header (if available) and parses the response as JSON.
 * Use as the `mutator`/`fetcher` in orval.config.ts for type-safe, authenticated API calls.
 *
 * @example
 *   const data = await orvalFetcher<MyResponseType>(url, options)
 */

const isoDateFormat
  = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/

function isIsoDateString(value: any): boolean {
  return value && typeof value === 'string' && isoDateFormat.test(value)
}

/**
 * Recursively converts ISO date strings to Date objects in the response data.
 * This is required when using orval with `useDates: true` and the fetch client.
 *
 * @param body The response data to process
 * @returns The data with ISO date strings converted to Date objects
 */
export function handleDates(body: any): any {
  if (body === null || body === undefined || typeof body !== 'object')
    return body

  for (const key of Object.keys(body)) {
    const value = body[key]
    if (isIsoDateString(value)) {
      body[key] = new Date(value) // Convert ISO string to Date object
    } else if (typeof value === 'object') {
      handleDates(value) // Recursively handle nested objects
    }
  }

  return body
}

const BASE_URL = import.meta.env.STORYBOOK
  ? import.meta.env.VITE_APP_MOCK_API_URL
  : import.meta.env.VITE_APP_API_URL

/** `fetch` options, plus the ones the API client understands itself. */
export interface FetchOptions extends RequestInit {
  /**
   * Leave a failed response to the caller: no error toast, and the rejection carries the server's
   * own message so the caller can show it next to the input that caused it.
   */
  silenceErrors?: boolean
}

export function createFetchWithAuth(baseUrl: string) {
  return async function <T>(
    url: string,
    options: FetchOptions = {},
  ): Promise<T> {
    const fullUrl = `${baseUrl}${url}`
    const { silenceErrors = false, ...init } = options
    const headers = await buildHeadersWithAuth(init)

    let response: Response

    try {
      response = await fetch(fullUrl, {
        ...init,
        headers,
      })
    } catch (error: Error | any) {
      if (error.name === 'AbortError') {
        // Don't catch abort errors, let them propagate
        throw error
      }

      const authStore = useAuthStore()

      authStore.setAuthState(AuthState.ServerNotReachable)

      return Promise.reject(new ServerError('Server not reachable', error))
    }

    if (response.status === 401) {
      useAuthStore().setAuthState(AuthState.ExpiredToken)

      return Promise.reject(new ServerError('Session expired', String(response.status)))
    }

    if (!response.ok) {
      return await handleErrorResponse(init, response, useAuthStore().toast, silenceErrors)
    }

    let data: any = undefined
    const contentLength = response.headers.get('content-length')
    const contentType = response.headers.get('content-type')

    // Only try to parse JSON if there is a body and it's JSON
    if (
      response.status !== 204 // 204 No Content
      && contentLength !== '0'
      && contentType
      && contentType.includes('application/json')
    ) {
      try {
        data = await response.json()
        // Convert ISO date strings to Date objects
        handleDates(data)
      } catch {
        data = null
      }
    } else if (contentType && !contentType.includes('application/json') && response.status !== 204) {
      // Return blob for binary responses (e.g., PDF downloads)
      data = await response.blob()
    }

    return data as T
  }
}

/**
 * A typed fetcher for Orval-generated API functions.
 *
 * This function appends the Authorization header (if available) and parses the response as JSON.
 * Use as the `mutator`/`fetcher` in orval.config.ts for type-safe, authenticated API calls.
 *
 * @param url The URL to fetch.
 * @param options The options to pass to the fetch function.
 * @returns The response data.
 */
const fetcher = createFetchWithAuth(BASE_URL)

/**
 * A typed fetcher for Orval-generated API functions.
 *
 * This function appends the Authorization header (if available) and parses the response as JSON.
 * Use as the `mutator`/`fetcher` in orval.config.ts for type-safe, authenticated API calls.
 *
 * @param url The URL to fetch.
 * @param options The options to pass to the fetch function.
 * @returns The response data.
 */
export async function fetchWithAuth<T>(
  url: string,
  options: FetchOptions = {},
): Promise<T> {
  return fetcher(url, options)
}

/** One message of a server-sent event stream: the event name and its (joined) data lines. */
export interface ServerSentEvent {
  event: string
  data: string
}

/**
 * POSTs JSON to the API and reads the response as server-sent events, calling `onEvent` for every
 * message until the stream ends. The counterpart of `fetchWithAuth` for the endpoints that answer
 * while they work (the job agent's determine run); the generated client cannot read those. A
 * non-OK response goes through the shared error handling (toast) and rejects.
 */
export async function streamWithAuth(url: string, body: unknown, onEvent: (event: ServerSentEvent) => void, signal?: AbortSignal): Promise<void> {
  const headers = await buildHeadersWithAuth({
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
  })
  const response = await fetch(`${BASE_URL}${url}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  })

  if (!response.ok || !response.body) {
    await handleErrorResponse({}, response, useAuthStore().toast)

    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const dispatch = (message: string) => {
    let event = 'message'
    const data: string[] = []

    for (const line of message.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        data.push(line.slice(5).trimStart())
      }
    }
    if (data.length > 0) {
      onEvent({
        event,
        data: data.join('\n'),
      })
    }
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const messages = buffer.split('\n\n')

    buffer = messages.pop() ?? ''
    messages.forEach(dispatch)
  }

  if (buffer.trim()) {
    dispatch(buffer)
  }
}

async function buildHeadersWithAuth(options: RequestInit): Promise<Headers> {
  const accessToken = await useAuthStore().getAccessToken?.()

  const headers = new Headers(options.headers || {})
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  /*
   * Forward the current PostHog session id so backend-emitted events (match close,
   * hire report, employer account/invite, matching config, etc.) land on the same
   * session timeline as the portal's client-side events. The API reads this from the
   * X-PostHog-Session-Id header; without it those events have no $session_id.
   * PostHog is only initialized in production, so the id is absent otherwise.
   */
  const sessionId = posthog.get_session_id?.()
  if (sessionId) {
    headers.set('X-PostHog-Session-Id', sessionId)
  }

  return headers
}